import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { User, Lock, Eye, EyeOff, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';

import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { API, setToken, steamLoginUrl } from '../api';
import SteamButton from './SteamButton';

function Login({ onLogin, steamError = '' }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState(steamError);
  const [loading, setLoading] = useState(false);

  const validatePassword = (pass) => {
    if (pass.length < 10) return 'Password must be at least 10 characters';
    if (!/[A-Z]/.test(pass)) return 'Must include an uppercase letter';
    if (!/[0-9]/.test(pass)) return 'Must include a number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isRegistering) {
      const err = validatePassword(password);
      if (err) {
        setError(err);
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tracker/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username,
          password,
          action: isRegistering ? 'register' : 'login',
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.sessionToken) setToken(data.sessionToken);
        // `account` says whether this login still owes a Steam link. Passing it
        // up means the caller can route straight to the gate without a second
        // request, and a server that has not been updated simply sends
        // undefined, which the caller treats as "not linked".
        onLogin(username, data.account);
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch {
      setError('Cannot reach the server. Make sure it is running.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      {/* static background image */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: 'url(/bg.png)' }}
      />
      {/* light scrim for legibility */}
      <div className="absolute inset-0 bg-black/30" />

      <div className="relative z-10 flex min-h-screen w-full items-center justify-center px-4 py-10">
        <div className="w-full max-w-[420px]">
          <Link
            to="/"
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="size-4" />
            Back to home
          </Link>
          <Card className="overflow-hidden rounded-2xl border-white/10 bg-[rgba(10,12,18,0.72)] shadow-[0_20px_60px_-25px_rgba(0,0,0,0.85)] backdrop-blur-xl">
            <CardContent className="p-8 pt-9 sm:p-10">
              {/* logo + title */}
              <div className="mb-7 flex flex-col items-center text-center">
                <img
                  src="/logo-pt-white.png"
                  alt="CS2 Profit Tracker"
                  className="mb-4 h-14 w-14 object-contain"
                  draggable={false}
                />
                <h1 className="text-[1.4rem] font-bold tracking-tight text-white">
                  CS2 Profit Tracker
                </h1>
                <p className="mt-1.5 text-sm text-muted-foreground">
                  {isRegistering
                    ? 'Create your account to start tracking'
                    : 'Sign in to your account'}
                </p>
              </div>

              {/* Steam is the primary way in — it is the only sign-in that
                  proves you own the inventory this app tracks, so it sits above
                  the form rather than beside it. */}
              <SteamButton href={steamLoginUrl()} />
              <p className="mt-2.5 text-center text-xs text-muted-foreground">
                No account needed. We only receive your Steam ID, name and avatar.
              </p>

              <div className="my-5 flex items-center gap-3">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-[0.91rem] font-medium uppercase tracking-wider text-muted-foreground">
                  or use a password
                </span>
                <div className="h-px flex-1 bg-white/10" />
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {/* username */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="username">Username</Label>
                  <div className="relative">
                    <User className="pointer-events-none absolute left-3.5 top-1/2 size-[1.05rem] -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      placeholder="Enter username"
                      autoComplete="username"
                      className="pl-11"
                    />
                  </div>
                </div>

                {/* password */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 size-[1.05rem] -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPw ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="Enter password"
                      autoComplete={isRegistering ? 'new-password' : 'current-password'}
                      className="pl-11 pr-11"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPw((v) => !v)}
                      aria-label={showPw ? 'Hide password' : 'Show password'}
                      className="absolute right-2.5 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-white"
                    >
                      {showPw ? <EyeOff className="size-[1.05rem]" /> : <Eye className="size-[1.05rem]" />}
                    </button>
                  </div>
                </div>

                {/* register hint */}
                {isRegistering && (
                  <div className="rounded-xl border border-white/5 bg-white/[0.02] px-3.5 py-2.5 text-xs text-muted-foreground">
                    Password must be 10+ characters, with 1 uppercase letter and 1 number.
                    You can connect a Steam account later from the Inventory tab.
                  </div>
                )}

                {/* error */}
                {error && (
                  <div className="flex items-start gap-2 rounded-xl border border-destructive/25 bg-destructive/10 px-3.5 py-2.5 text-sm text-destructive">
                    <AlertCircle className="mt-0.5 size-4 shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                {/* submit */}
                <Button type="submit" disabled={loading} className="mt-1 w-full">
                  {loading ? (
                    <>
                      <Loader2 className="size-4 animate-spin" />
                      Please wait…
                    </>
                  ) : isRegistering ? (
                    'Create Account'
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>

              {/* toggle */}
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => {
                    setIsRegistering((v) => !v);
                    setError('');
                  }}
                  className="text-sm text-muted-foreground transition-colors hover:text-white"
                >
                  {isRegistering ? (
                    <>Already have an account? <span className="font-semibold text-primary">Sign in</span></>
                  ) : (
                    <>Don&rsquo;t have an account? <span className="font-semibold text-primary">Register</span></>
                  )}
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Login;
